<template>
    <v-dialog v-model="dialog" max-width="500" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('common.add_block_to_routine') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12">
                        <kimsa-combobox :label="$t('common.folder')" outlined dense :items="folderOptions"
                            @change="folder = $event" ref="folders" />
                    </v-col>
                    <v-col cols="10">
                        <kimsa-combobox :label="$t('common.name')" outlined dense :items="blockOptions"
                            @change="block = $event" ref="blocks" can-write no-create-exercise :loading="loadingSearch"
                            @search="search = $event" :readonly="!folder" />
                    </v-col>
                    <v-col cols="2" class="p-relative">
                        <v-btn class="mt-5" icon @click="showPreview = !showPreview" :disabled="!block" color="secondary">
                            <v-icon>mdi-eye</v-icon>
                        </v-btn>
                        <v-dialog v-model="showPreview" @click:outside="showPreview = false" max-width="300">
                            <v-card flat class="block-exercises-container kimsa-elevation pa-3">
                                <exercise-event v-for="item in blockExercises" :key="item.uid" :event="item"
                                    is-hover-view />
                            </v-card>
                        </v-dialog>
                    </v-col>
                    <v-col cols="12">
                        <kimsa-combobox :label="$t('common.block_position')" outlined dense :items="positionOptions"
                            @change="position = $event" ref="positions" />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabled" @click="save" :loading="loading">
                    {{ $t('common.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";
import { Client, BlockIndex } from "@/utils/Algolia";
import { debounce } from '@/utils/Common'
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { updateAssignedRoutinesGroup, getPayloadBaseAssignedRoutine, deleteAssignedRoutinesGroup, addAssignedRoutinesGroup, clearBlock } from "@/utils/Exercises"
import ExerciseEvent from '../../calendar/ExerciseEvent.vue';

export default {
    name: "AddBlockDialog",
    components: { KimsaCombobox, ExerciseEvent },
    props: {
        currentSelection: {
            type: Object,
            default: null,
        },
        currentRoutines: {
            type: Array,
            default: null,
        },
        collection: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null
        },
        assignedRoutineToReplace: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            assignedRoutine: null,
            folder: null,
            blockOptions: [],
            block: null,
            position: null,
            search: '',
            loadingSearch: false,
            date: null,
            blockColor: '#66CCCC',
            showPreview: false,
        }
    },
    computed: {
        ...mapState(['dialogActive', 'folders', 'globalFolders']),
        disabled(vm = this) {
            return !vm.folder || !vm.block || !vm.position
        },
        previewImg(vm = this) {
            return vm.file?.img || null
        },
        folderOptions(vm = this) {
            return [...vm.globalFolders, ...vm.folders].filter(folder => folder.type === 'blocks') || []
        },
        positionOptions(vm = this) {
            return Array.from({ length: (vm.assignedRoutine?.blocks?.length || 0) + 1 }, (_, i) => i + 1)
        },
        blockExercises(vm = this) {
            let block = clearBlock(vm.block?.block || null)
            let exercises = []
            block?.exercises?.forEach(exercise => {
                if (exercise.type === 'exercise') exercises.push({
                    event: {
                        ...exercise,
                        color: vm.blockColor,
                        isCircuit: block?.isCircuit || false,
                        sets: block?.sets || null,
                    }
                })
            })
            return exercises
        },
    },
    async mounted() {
        const vm = this
        // console.log(vm)
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        ...mapActions(['bindFolders', 'bindGlobalFolders']),
        show(date) {
            const vm = this
            vm.loadRoutine(date)
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.assignedRoutine = null
            if (vm.$refs.folders) vm.$refs.folders.model = null
            if (vm.$refs.blocks) vm.$refs.blocks.model = null
            if (vm.$refs.positions) vm.$refs.positions.model = null
            vm.block = null
            vm.folder = null
            vm.position = null
            vm.blockOptions = []
            vm.search = ''
        },
        loadRoutine(date) {
            const vm = this;
            vm.date = date
            vm.assignedRoutine = vm.currentRoutines.find(routine => routine.rawDate.full === date)
        },
        debouncedLoadBlockOptions: debounce(
            function () {
                this.loadBlockOptions()
            }, 1000),
        async loadBlockOptions() {
            const vm = this;
            if (!vm.folder) return
            vm.loadingSearch = true
            let filter = `coachId: ${vm.coachId} AND folder: ${vm.folder.id}`
            await Client.clearCache()
            let response = await BlockIndex.search(vm.search, {
                filters: filter,
                hitsPerPage: 100,
            })
            vm.blockOptions = response.hits
            vm.loadingSearch = false
        },
        async addNewAssignedRoutine(blocks) {
            const vm = this

            const name = `${vm.currentSelection.name} - ${vm.$moment(vm.date).format('DD-MM-YYYY')}`
            let payloadAssignedRoutine = getPayloadBaseAssignedRoutine(name, vm.date, { coachId: vm.coachId }, blocks)

            if (vm.collection === 'assignedRoutines') {
                payloadAssignedRoutine.studentId = vm.currentSelection.objectID
            } else {
                payloadAssignedRoutine.teamId = vm.currentSelection.teamId || null
                payloadAssignedRoutine.groupId = vm.currentSelection.id
            }

            const arRef = await db.collection(vm.collection).add(payloadAssignedRoutine)
            const assignedRoutineId = arRef.id

            if (vm.collection === 'groupAssignedRoutines') {
                let students = [...vm.currentSelection.students]
                if (vm.assignedRoutineToReplace.length > 0) {
                    let toDelete = vm.assignedRoutineToReplace.filter(ar => ar.replace).map(ar => {
                        return ar.assignedRoutineId
                    })
                    if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)
                    let skippedStudents = vm.assignedRoutineToReplace.filter(ar => !ar.replace).map(ar => {
                        return ar.studentId
                    })
                    if (skippedStudents.length > 0) students = students.filter(st => !skippedStudents.includes(st))
                }
                await addAssignedRoutinesGroup({ id: assignedRoutineId, ...payloadAssignedRoutine, date: vm.date }, students)
            }

            vm.assignedRoutine = { id: assignedRoutineId, ...payloadAssignedRoutine }
        },
        async save() {
            const vm = this
            vm.loading = true

            if (!vm.assignedRoutine) {
                await vm.addNewAssignedRoutine([vm.block.block])
            } else {
                let blocks = vm.assignedRoutine.blocks
                blocks.splice(vm.position - 1, 0, vm.block.block)
                await db.collection(vm.collection).doc(vm.assignedRoutine.id).update({ blocks: blocks.map((b, i) => ({ ...b, id: i + 1 })) })
                if (vm.collection === 'groupAssignedRoutines') await updateAssignedRoutinesGroup(vm.assignedRoutine.assignedRoutinesIds, blocks)
            }

            await db.collection('blocks').doc(vm.block.objectID).update({ used: FieldValue.increment(1) })
            vm.$emit('success', {
                assignedRoutineId: vm.assignedRoutine.id,
                routineId: vm.assignedRoutine.routineId || null,
                date: vm.assignedRoutine.rawDate.full
            })
            vm.loading = false
            vm.hide()
            vm.toast(vm.$t('common.saved_block'))
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        folder() {
            this.loadBlockOptions()
        },
        search() {
            this.debouncedLoadBlockOptions()
        }
    }
}
</script>

<style scoped>
.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}
</style>